import { Currency, CurrencyAmount, TradeType } from '@kodiak-finance/sdk-core'
import { SWAP_QUOTE_REFETCH_POLICY } from 'config/RefetchPolicies'
import { useFetchKxQuote } from 'hooks/backend/useFetchKxQuote'
import { useConnection } from 'hooks/useConnection'
import { stablecoinAmountFromFiatValue, useStablecoinAmountFromFiatValue } from 'hooks/useStablecoinPrice'
import { useRoutingAPIArguments } from 'lib/hooks/routing/useRoutingAPIArguments'
import { useIsValidBlock } from 'lib/hooks/useIsValidBlock'
import { useEffect, useMemo } from 'react'
import { zeroAddress } from 'viem'

import { ExecutionExplanation, KodiakQuote, QuoteResult, Trade, TradeFactory, TradeState } from './trades'

type RankInputs = {
  explanations: Map<string, ExecutionExplanation>
  bestProvider: string
  tokenOutPriceUSD: number
}

// Scores a route the way the backend ranked it: the promised output in dollars after gas, weighted
// by provider priority. A route it rejected after simulating is scored by what the simulation paid,
// otherwise its inflated promise would appear to beat the route we actually chose.
function rankValue(provider: string, gasUseEstimateUSD: string, inputs: RankInputs): number | undefined {
  const explanation = inputs.explanations.get(provider)
  if (!explanation) return undefined

  if (provider !== inputs.bestProvider && explanation.simulatedAmountOut) {
    if (!inputs.tokenOutPriceUSD) return undefined
    const priority = Number(explanation.priority) || 1
    const gasUSD = Number(gasUseEstimateUSD) || 0
    const score = Number(explanation.simulatedAmountOut) * priority * inputs.tokenOutPriceUSD - gasUSD
    return Number.isFinite(score) ? score : undefined
  }

  const adjusted = Number(explanation.adjustedQuoteDecimals)
  return Number.isFinite(adjusted) ? adjusted : undefined
}

/**
 * Returns the best trade by invoking the routing api
 * @param tradeType whether the swap is an exact in/out
 * @param providers array of provider names to include in aggregation
 * @param amountSpecified the exact amount to swap in/out
 * @param otherCurrency the desired output/payment currency
 * @param pauseFetch pause fetching if true
 * @param resolvedRecipient recipient address for the trade
 */
export function useRoutingAPITrade(
  tradeType: TradeType,
  providers: string[],
  amountSpecified?: CurrencyAmount<Currency>,
  otherCurrency?: Currency,
  pauseFetch?: boolean,
  resolvedRecipient?: string | null
): {
  state: TradeState
  trades: Trade[]
  bestTrade: Trade | undefined
  refetch: () => void
  isRefetching: boolean
} {
  const [currencyIn, currencyOut]: [Currency | undefined, Currency | undefined] = useMemo(
    () =>
      tradeType === TradeType.EXACT_INPUT
        ? [amountSpecified?.currency, otherCurrency]
        : [otherCurrency, amountSpecified?.currency],
    [amountSpecified, otherCurrency, tradeType]
  )

  const queryArgs = useRoutingAPIArguments({
    tokenIn: currencyIn,
    tokenOut: currencyOut,
    amount: amountSpecified,
    tradeType,
    providers,
    recipient: resolvedRecipient,
  })

  const { isLoading, isError, isFetching, data, refetch /*error*/ } = useFetchKxQuote(queryArgs ?? undefined, {
    ...SWAP_QUOTE_REFETCH_POLICY,
    skip: pauseFetch || !queryArgs || !providers.length,
  })

  const { chainId } = useConnection()
  const isValidBlock = useIsValidBlock(Number(data?.blockNumber) || 0)

  useEffect(() => {
    console.log(`DEBUG:: quote block: ${data?.blockNumber}, isValidBlock: ${isValidBlock}`)
  }, [isValidBlock, data?.blockNumber])

  const isStaleQuote = !!data && !isValidBlock
  const quoteResult: QuoteResult | undefined = isValidBlock ? data : undefined

  const bestPriceGasUseEstimateUSD = useStablecoinAmountFromFiatValue(quoteResult?.gasUseEstimateUSD) ?? null
  const kodiakPriceGasUseEstimateUSD =
    useStablecoinAmountFromFiatValue(quoteResult?.otherQuote?.gasUseEstimateUSD) ?? null

  const trades = useMemo(() => {
    if (!quoteResult || !currencyIn || !currencyOut) return []

    const quoteInputAddress = quoteResult.params?.tokenInAddress
    const quoteOutputAddress = quoteResult.params?.tokenOutAddress
    const currentInputAddress = currencyIn?.isNative ? zeroAddress : currencyIn?.address
    const currentOutputAddress = currencyOut?.isNative ? zeroAddress : currencyOut?.address

    if (
      !quoteInputAddress ||
      !currentInputAddress ||
      !quoteOutputAddress ||
      !currentOutputAddress ||
      quoteInputAddress?.toLowerCase() !== currentInputAddress?.toLowerCase() ||
      quoteOutputAddress?.toLowerCase() !== currentOutputAddress?.toLowerCase()
    ) {
      return []
    }

    const trades: Trade[] = []
    const isExactOut = quoteResult.params.type === 'exactOut'
    const hasMainCalldata = !!(quoteResult.methodParameters?.calldata && quoteResult.methodParameters?.to)

    // Main trade option (best price from aggregation)
    const rankInputs: RankInputs = {
      explanations: new Map((quoteResult.expl ?? []).map((explanation) => [explanation.provider, explanation])),
      bestProvider: quoteResult.provider,
      tokenOutPriceUSD: Number(quoteResult.tokenOutPriceUSD),
    }

    if (quoteResult.quote) {
      try {
        const isKXRoute = true
        const mainTrade = TradeFactory.createFromQuote(
          quoteResult,
          currencyIn,
          currencyOut,
          tradeType,
          bestPriceGasUseEstimateUSD,
          'main',
          {
            refFee: quoteResult.refFee,
            isBestPrice: true,
            canExecute: hasMainCalldata && !(isKXRoute && isExactOut),
            isDisabledForExactOut: isKXRoute && isExactOut,
            rankValue: rankValue(quoteResult.provider, quoteResult.gasUseEstimateUSD, rankInputs),
          }
        )

        trades.push(mainTrade)
      } catch (error) {
        console.warn(`Failed to create trade for ${quoteResult.provider}:`, error)
      }
    }

    for (const alternative of quoteResult.alternatives ?? []) {
      try {
        const hasCalldata = Boolean(alternative.methodParameters?.calldata && alternative.methodParameters?.to)
        trades.push(
          TradeFactory.createFromQuote(
            alternative,
            currencyIn,
            currencyOut,
            tradeType,
            stablecoinAmountFromFiatValue(alternative.gasUseEstimateUSD, chainId) ?? null,
            'main',
            {
              refFee: alternative.refFee,
              isBestPrice: false,
              canExecute: hasCalldata && !isExactOut,
              isDisabledForExactOut: isExactOut,
              rankValue: rankValue(alternative.provider, alternative.gasUseEstimateUSD, rankInputs),
            }
          )
        )
      } catch (error) {
        console.warn(`Failed to create trade for ${alternative.provider}:`, error)
      }
    }

    // Kodiak-only routes through the Kodiak router, the one path that executes exact-output trades.
    // The UI hides exact output for now, so this stays dormant until it returns; for exact input
    // Kodiak already competes among the kX alternatives and needs no route of its own.
    const otherProvider = quoteResult.otherQuote?.provider.toLowerCase()
    const kodiakQuote: KodiakQuote | undefined =
      otherProvider === 'kodiak' ? (quoteResult.otherQuote as KodiakQuote) : undefined
    if (kodiakQuote && isExactOut) {
      try {
        const hasKodiakCalldata = !!(kodiakQuote.methodParameters?.calldata && kodiakQuote.methodParameters?.to)

        const kodiakTrade = TradeFactory.createFromQuote(
          kodiakQuote,
          currencyIn,
          currencyOut,
          tradeType,
          kodiakPriceGasUseEstimateUSD,
          'kodiak-only',
          {
            refFee: quoteResult.refFee,
            isBestPrice: false,
            canExecute: hasKodiakCalldata,
            isDisabledForExactOut: false,
          }
        )
        trades.push(kodiakTrade)
      } catch (error) {
        console.warn('Failed to create Kodiak trade:', error)
      }
    }

    return trades
  }, [
    quoteResult,
    currencyIn,
    currencyOut,
    tradeType,
    bestPriceGasUseEstimateUSD,
    kodiakPriceGasUseEstimateUSD,
    chainId,
  ])

  const bestTrade = useMemo(() => {
    if (!currencyIn || !currencyOut) return undefined

    const bestExecutable = trades.find((trade) => trade.isBestPrice)

    return bestExecutable
  }, [trades, currencyIn, currencyOut])

  return useMemo(() => {
    const isRefetching = isFetching && !isLoading
    const isSyncing = isRefetching
    if (!currencyIn || !currencyOut) {
      return {
        state: TradeState.INVALID,
        trades: [],
        bestTrade: undefined,
        refetch: () => Promise.resolve(),
        isRefetching: false,
      }
    }

    if (isStaleQuote) {
      return {
        state: TradeState.STALE,
        trades: [],
        bestTrade: undefined,
        refetch,
        isRefetching,
      }
    }

    if (isLoading && !quoteResult) {
      return {
        state: TradeState.LOADING,
        trades: [],
        bestTrade: undefined,
        refetch,
        isRefetching,
      }
    }

    if (isError && !isLoading && !quoteResult) {
      return {
        state: TradeState.NO_ROUTE_FOUND,
        trades: [],
        bestTrade: undefined,
        refetch,
        isRefetching,
      }
    }

    if (isSyncing || (isFetching && !quoteResult)) {
      return {
        state: TradeState.SYNCING,
        trades,
        bestTrade,
        refetch,
        isRefetching,
      }
    }

    if (trades.length === 0) {
      return {
        state: TradeState.NO_ROUTE_FOUND,
        trades: [],
        bestTrade: undefined,
        refetch,
        isRefetching,
      }
    }

    return {
      state: TradeState.VALID,
      trades,
      bestTrade,
      refetch,
      isRefetching,
    }
  }, [currencyIn, currencyOut, isLoading, quoteResult, isError, isFetching, trades, bestTrade, refetch, isStaleQuote])
}
